<script setup lang="ts">
import { t } from "../i18n";
import { ref, computed, onMounted, watch } from "vue";
import IconButton from "./IconButton.vue";
import SearchField from "./SearchField.vue";
import {
  emojiCategories,
  loadEmojis,
  filterEmojis,
  type EmojiEntry,
} from "../data/emojis";
const props = defineProps<{
  reactionsOnly?: boolean;
  /** Emoji only, without the GIF and sticker tabs (the image editor). */
  emojiOnly?: boolean;
  selected?: string;
  autofocus?: boolean;
}>();
const emit = defineEmits<{
  choose: [text: string, kind: "text" | "sticker" | "image"];
  close: [];
}>();
const root = ref<HTMLElement>(),
  scroll = ref<HTMLElement>();
const entries = ref<EmojiEntry[]>([]),
  loading = ref(true),
  error = ref(false);
const tab = ref("emoji"),
  search = ref(""),
  category = ref(0);
const groups = computed(() => {
  const matches = filterEmojis(entries.value, search.value, category.value);
  return emojiCategories
    .map((group, index) => ({
      ...group,
      items: matches.filter((emoji) => emoji.category === index),
    }))
    .filter((group) => group.items.length);
});
async function loadCatalog() {
  loading.value = true;
  error.value = false;
  try {
    entries.value = await loadEmojis();
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
}
function focusSearch() {
  root.value?.querySelector("input")?.focus({ preventScroll: true });
}
onMounted(() => {
  loadCatalog();
  if (props.autofocus) focusSearch();
});
watch(
  [search, category, tab],
  () => scroll.value?.scrollTo({ top: 0, behavior: "instant" }),
  { flush: "post" },
);
function gridKey(event: KeyboardEvent) {
  if (
    ![
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ].includes(event.key)
  )
    return;
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
    ".emoji-grid button",
  );
  if (!button) return;
  const buttons = [
    ...(scroll.value?.querySelectorAll<HTMLButtonElement>(
      ".emoji-grid button",
    ) || []),
  ];
  const index = buttons.indexOf(button);
  const columns = getComputedStyle(
    button.parentElement!,
  ).gridTemplateColumns.split(" ").length;
  const next =
    event.key === "Home"
      ? 0
      : event.key === "End"
        ? buttons.length - 1
        : index +
          ({
            ArrowLeft: -1,
            ArrowRight: 1,
            ArrowUp: -columns,
            ArrowDown: columns,
          }[event.key] || 0);
  event.preventDefault();
  buttons[Math.max(0, Math.min(next, buttons.length - 1))]?.focus();
}
defineExpose({ focusSearch });
</script>
<template>
  <section
    ref="root"
    class="emoji-picker"
    :aria-label="reactionsOnly ? t('emoji.pick_reaction') : t('emoji.panel')"
  >
    <header class="row">
      <div class="row grow" style="gap: 2px">
        <IconButton
          v-for="(g, i) in emojiCategories"
          :key="g.name"
          :icon="g.icon"
          :label="g.name"
          :active="category === i"
          :size="18"
          @click="
            category = i;
            search = '';
            tab = 'emoji';
          "
        />
      </div>
      <IconButton
        icon="close"
        :label="reactionsOnly ? t('emoji.back_to_quick') : t('emoji.close')"
        @click="emit('close')"
      />
    </header>
    <SearchField
      v-model="search"
      :placeholder="
        reactionsOnly
          ? t('emoji.search_reaction')
          : tab === 'emoji'
            ? t('emoji.search_emoji')
            : tab === 'gif'
              ? t('emoji.search_gif')
              : t('emoji.search_sticker')
      "
    />
    <div ref="scroll" class="emoji-scroll scroll" @keydown="gridKey">
      <template v-if="tab === 'emoji'"
        ><p v-if="loading" role="status" class="muted">Carregando emojis…</p>
        <div v-else-if="error" class="muted">
          Não foi possível carregar os emojis.
          <button class="green" @click="loadCatalog">Tentar novamente</button>
        </div>
        <div v-for="g in groups" :key="g.name">
          <p class="small muted">{{ g.name }}</p>
          <div class="emoji-grid">
            <button
              v-for="emoji in g.items"
              :key="emoji.emoji"
              type="button"
              :aria-label="emoji.emoji + ' ' + emoji.label"
              :aria-pressed="selected === emoji.emoji"
              :class="{ selected: selected === emoji.emoji }"
              @click="emit('choose', emoji.emoji, 'text')"
            >
              {{ emoji.emoji }}
            </button>
          </div>
        </div>
        <p v-if="!loading && !error && !groups.length" class="muted">
          Nenhum emoji encontrado. Pesquise por nome, como “coração”, “café” ou
          “Brasil”.
        </p></template
      >
      <div v-else-if="tab === 'sticker'" class="sticker-grid">
        <button
          v-for="(s, i) in [
            '🌈',
            '💚',
            '✨',
            '🥳',
            '👋',
            '☕',
            '🌻',
            '😎',
            '🦋',
          ]"
          :key="s"
          :aria-label="'Enviar figurinha ' + s"
          @click="emit('choose', s, 'sticker')"
        >
          <span
            :style="{ transform: 'rotate(' + ((i % 3) - 1) * 10 + 'deg)' }"
            >{{ s }}</span
          >
        </button>
      </div>
      <div v-else class="gif-grid">
        <button
          v-for="(s, i) in [
            'Bom dia!',
            'Tudo certo',
            'Obrigada!',
            'Partiu!',
            'Boa ideia',
            'Até logo',
          ]"
          :key="s"
          @click="emit('choose', s, 'sticker')"
        >
          <span :style="{ animationDelay: i * 0.1 + 's' }">{{ s }}</span>
        </button>
      </div>
    </div>
    <footer v-if="!reactionsOnly && !emojiOnly" class="row">
      <button
        :class="{ active: tab === 'emoji' }"
        @click="
          tab = 'emoji';
          search = '';
        "
      >
        Emojis</button
      ><button
        :class="{ active: tab === 'gif' }"
        @click="
          tab = 'gif';
          search = '';
        "
      >
        GIFs</button
      ><button
        :class="{ active: tab === 'sticker' }"
        @click="
          tab = 'sticker';
          search = '';
        "
      >
        Figurinhas
      </button>
    </footer>
  </section>
</template>
